import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Field } from 'redux-form';
import ReactCrop, { makeAspectCrop } from 'react-image-crop';
import { Button, CircularProgress } from 'material-ui';
import 'react-image-crop/dist/ReactCrop.css';
import {
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_RECEIPT_UPLOAD_PRESET,
} from '../../../../../config';

const styles = {
  uploadButtonStyle: {
    cursor: 'pointer',
    position: 'absolute',
    top: '0',
    bottom: '0',
    right: '0',
    left: '0',
    width: '100%',
    height: '100%',
    opacity: '0',
    zIndex: 10,
  },
  isUploadingStyle: {
    position: 'absolute',
    zIndex: 1,
    opacity: 0.6,
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgb(80,80,80)',
  },
  imageStyle: {
    border: '1px solid #EEE',
    marginBottom: '10px',
    maxHeight: '100px',
  },
  button: {
    width: 'auto',
    margin: '10px 10px 10px 0px',
    border: '1px solid #000',
    fontSize: '12px',
    padding: '4px 12px',
  },
  encodedFile: {
    position: 'relative',
    display: 'inline-flex',
  },
  noticeStyle: {
    fontSize: '13px',
    color: '#F44336',
    margin: '0',
  },
};

const encodeFile = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
  });

class ImageInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      fileType: null,
      encodedFile: null,
      crop: {},
      isUploading: false,
    };
  }

  onChange = async (event) => {
    const [firstFile] = event.target.files;

    if (!firstFile) {
      return;
    }

    const encodedFile = await encodeFile(firstFile);

    this.setState({
      fileType: firstFile.type,
      encodedFile,
    });
  };

  onImageLoaded = (image) => {
    const { aspect } = this.props;
    const { width, height } = image;

    let crop;

    if (aspect) {
      crop = makeAspectCrop(
        {
          x: 0,
          y: 0,
          aspect,
          width: 50,
        },
        width / height
      );
    } else {
      crop = {
        x: 0,
        y: 0,
        width: 100,
        height: 100,
      };
    }

    this.setState({ crop });
  };

  uploadImage = async (file) => {
    const {
      input: { onChange },
    } = this.props;

    try {
      this.setState({ isUploading: true });

      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload/`,
        {
          file,
          // https://cloudinary.com/documentation/image_upload_api_reference#upload
          // Upload preset required for unsigned uploading
          upload_preset: CLOUDINARY_RECEIPT_UPLOAD_PRESET,
        }
      );
      this.setState(
        { isUploading: false, encodedFile: null, crop: null },
        () => {
          onChange(response.data.public_id);
        }
      );
    } catch (error) {
      this.setState({ isUploading: false }, () => {
        console.error('receipt upload error: ', error.message); // eslint-disable-line no-console
      });
    }
  };

  updateCrop = (crop) => {
    this.setState({ crop });
  };

  cropImage = () => {
    const { encodedFile, crop, fileType } = this.state;

    if (fileType === 'application/pdf') {
      this.uploadImage(encodedFile);
    } else {
      const loadedImg = new Image();
      loadedImg.src = encodedFile;
      const imageWidth = loadedImg.naturalWidth;
      const imageHeight = loadedImg.naturalHeight;
      const cropX = (crop.x / 100) * imageWidth;
      const cropY = (crop.y / 100) * imageHeight;
      const cropWidth = (crop.width / 100) * imageWidth;
      const cropHeight = (crop.height / 100) * imageHeight;
      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        loadedImg,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      this.uploadImage(canvas.toDataURL('image/png'));
    }
  };

  renderImage() {
    const {
      disabled,
      input: { value },
      meta: { touched, error },
    } = this.props;
    const { isUploading, encodedFile, fileType } = this.state;

    if (encodedFile) {
      return (
        <div>
          <div style={styles.encodedFile}>
            {isUploading && (
              <div style={styles.isUploadingStyle}>
                <CircularProgress />
              </div>
            )}

            {fileType === 'application/pdf' ? (
              <embed
                src={encodedFile}
                type='application/pdf'
                style={styles.imageStyle}
                width='100'
                height='100'
              />
            ) : (
              <ReactCrop
                disabled
                imageStyle={styles.imageStyle}
                src={encodedFile}
                onChange={this.updateCrop}
                crop={this.state.crop}
                onImageLoaded={this.onImageLoaded}
              />
            )}
          </div>
          {touched && error ? <p style={styles.noticeStyle}>{error}</p> : null}
        </div>
      );
    }

    if (!value) {
      return touched && error ? (
        <p style={styles.noticeStyle}>{error}</p>
      ) : null;
    }

    return (
      <div>
        <img
          alt='receipt'
          src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/f_auto,q_100,w_1400/v1/${value}`}
          style={styles.imageStyle}
          className='img-checkered-bg'
        />
        {!disabled && (
          <p style={styles.noticeStyle}>
            * Proceed to the next step to save reciept
          </p>
        )}
      </div>
    );
  }

  render() {
    const { recommendedAspect, disabled } = this.props;
    const { encodedFile, isUploading } = this.state;

    return (
      <div>
        {encodedFile ? (
          <div style={{ display: 'block' }}>
            <Button
              variant='outlined'
              disabled={isUploading}
              style={styles.button}
              onClick={() => this.setState({ crop: null, encodedFile: null })}
            >
              Cancel
            </Button>
            <Button
              variant='outlined'
              disabled={isUploading}
              style={styles.button}
              onClick={this.cropImage}
            >
              Confirm
            </Button>
            {recommendedAspect && (
              <p style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '11px' }}>
                Recommended aspect ratio: <strong>{recommendedAspect}</strong>
              </p>
            )}
          </div>
        ) : (
          !disabled && (
            <Button style={styles.button}>
              <input
                type='file'
                accept='image/*,application/pdf'
                style={styles.uploadButtonStyle}
                onChange={this.onChange}
              />
              Choose File
            </Button>
          )
        )}
        {this.renderImage()}
      </div>
    );
  }
}

const FieldWrapper = ({
  source,
  aspect,
  recommendedAspect,
  validate,
  disabled,
}) => (
  <Field
    name={source}
    disabled={disabled}
    component={ImageInput}
    aspect={aspect}
    recommendedAspect={recommendedAspect}
    validate={validate}
  />
);

ImageInput.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  aspect: PropTypes.number,
  recommendedAspect: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
};

ImageInput.defaultProps = {
  aspect: undefined,
  recommendedAspect: undefined,
};

FieldWrapper.propTypes = {
  source: PropTypes.string.isRequired,
  aspect: PropTypes.number,
  recommendedAspect: PropTypes.string,
  validate: PropTypes.func,
  disabled: PropTypes.bool,
};

FieldWrapper.defaultProps = {
  aspect: undefined,
  recommendedAspect: undefined,
  validate: null,
  disabled: false,
};

export default FieldWrapper;



// WEBPACK FOOTER //
// ./src/components/OfferOverview/ReadyToInvestButton/PaymentProcess/PaymentProcessForm/ImageInput.js